import { orderSessionSelection } from "./sessionSelectionOrder";
import { fetchPrimary } from "../environments/primary/fetch";
import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
import { parseGoalRelationships, type GoalRelationships } from "@cleo/contracts";
import {
  rootSessionsOfRoom,
  canonicalWorkroomLabel,
  WorldRoomEntry,
  WorldRoomIndex,
  WorldRoomIndexPage,
  WORLD_SESSION_TIMES_MAX,
  WorldSessionTimes,
  type WorldRoomDirectory,
  type WorldRoomIndex as WorldRoomIndexValue,
} from "@cleo/contracts";
import type { WorldRoomGoalProjection, WorldSessionSelectionBirth } from "@cleo/contracts";
import * as Schema from "effect/Schema";

import type { WorldContextSelection, RoomUniverseEntry } from "./roomUniverseStore";
import { useNeucleosWorldStore } from "./worldStore";

const decodeWorldRoomIndex = Schema.decodeUnknownSync(WorldRoomIndex);
const decodeWorldRoomIndexPage = Schema.decodeUnknownSync(WorldRoomIndexPage);

export type WorldSessionClientOutcome =
  | { readonly ok: true; readonly index: WorldRoomIndexValue; readonly created?: boolean }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

export type WorldRoomEntryOutcome =
  | { readonly ok: true; readonly entry: WorldRoomEntry; readonly created: boolean }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/** A change to one room or session, confirmed by the World's read of that entry. */
export type WorldEntryChangeOutcome =
  | { readonly ok: true; readonly entry: WorldRoomEntry }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

/**
 * The root session a room-level change (rename, focus, archive) names: the
 * World's exact read is a room and one of its sessions, and the door reads
 * back that entry instead of the whole directory (W-c338b645).
 */
export function rootSessionIdOf(index: WorldRoomDirectory, roomId: string): string | undefined {
  return rootSessionsOfRoom(index, roomId)[0]?.sessionId;
}

/**
 * Post one change and accept only the entry the door read back after it: the
 * addressed session (and its room), valid for this World, that `validate`
 * confirms. The directory is never read whole right after a write: on a World
 * of 752 sessions that read outlived the door's time limit.
 */
export async function confirmChangedEntry(
  path: string,
  input: Record<string, unknown> & { readonly worldKey: string },
  sessionId: string,
  validate: (entry: WorldRoomEntry) => boolean,
  fetchImpl: typeof fetch,
  refused: (
    response: Response,
    body: Record<string, unknown> | null,
  ) => Extract<WorldEntryChangeOutcome, { ok: false }> = refusal,
): Promise<WorldEntryChangeOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, input, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refused(response, body);
  try {
    const entry = Schema.decodeUnknownSync(WorldRoomEntry)(body["entry"]);
    if (
      entry.worldKey === input.worldKey &&
      entry.session.sessionId === sessionId &&
      validate(entry)
    )
      return { ok: true, entry };
  } catch {
    /* An invalid or cross-World readback does not confirm the change. */
  }
  return {
    ok: false,
    kind: "transport",
    reason: "The World did not confirm the change.",
  };
}

export async function readWorldRoomEntry(
  input: { readonly worldKey: string } & (
    | { readonly roomId?: string; readonly sessionId: string; readonly latestRoot?: never }
    | { readonly roomId: string; readonly latestRoot: true; readonly sessionId?: never }
  ),
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldRoomEntryOutcome> {
  let response: Response;
  try {
    const { worldKey, ...entry } = input;
    response = await postLocalJson("/api/neucleos/world/rooms", { worldKey, entry }, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  try {
    const entry = Schema.decodeUnknownSync(WorldRoomEntry)(body["entry"]);
    if (
      entry.worldKey === input.worldKey &&
      (input.roomId === undefined || entry.room.roomId === input.roomId) &&
      (input.latestRoot
        ? entry.session.parentId === input.roomId
        : entry.session.sessionId === input.sessionId)
    )
      return { ok: true, entry, created: false };
  } catch {
    /* A malformed or cross-World entry is never navigation authority. */
  }
  return {
    ok: false,
    kind: "transport",
    reason: "The World did not confirm the requested room entry.",
  };
}

/** When a session was last active in the World (ms), and whether the World says it is retired. */
export interface WorldSessionTime {
  readonly activeAt: number;
  readonly retired: boolean;
}

/**
 * Can 80: when each of these sessions was last active in the World (ms), and
 * whether it is retired, read WORLD_SESSION_TIMES_MAX at a time. A session the
 * World leaves out has no time here; a World from before `retired` says none
 * is. Undefined when any read fails: the caller keeps what it had.
 */
export async function readWorldSessionTimes(
  worldKey: string,
  sessionIds: ReadonlyArray<string>,
  fetchImpl: typeof fetch = fetchPrimary,
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, WorldSessionTime> | undefined> {
  const times = new Map<string, WorldSessionTime>();
  const ids = [...new Set(sessionIds)];
  for (let at = 0; at < ids.length; at += WORLD_SESSION_TIMES_MAX) {
    const asked = ids.slice(at, at + WORLD_SESSION_TIMES_MAX);
    try {
      const response = await postLocalJson(
        "/api/neucleos/world/rooms",
        { worldKey, sessionTimes: { sessionIds: asked } },
        fetchImpl,
        signal,
      );
      const body = await decodeJsonObject(response);
      if (!response.ok || body?.["ok"] !== true) return undefined;
      const read = Schema.decodeUnknownSync(WorldSessionTimes)(body["times"]);
      if (read.worldKey !== worldKey) return undefined;
      for (const row of read.sessions)
        if (asked.includes(row.sessionId))
          times.set(row.sessionId, { activeAt: row.activeAt, retired: row.retired === true });
    } catch {
      return undefined;
    }
  }
  return times;
}

async function openEntry(
  path: string,
  input: Record<string, unknown> & { readonly worldKey: string; readonly sessionId: string },
  validate: (entry: WorldRoomEntry) => boolean,
  fetchImpl: typeof fetch,
): Promise<WorldRoomEntryOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(path, { ...input, readback: "entry" }, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  try {
    const entry = Schema.decodeUnknownSync(WorldRoomEntry)(body["entry"]);
    if (
      entry.worldKey === input.worldKey &&
      entry.session.sessionId === input.sessionId &&
      validate(entry)
    )
      return { ok: true, entry, created: body["created"] === true };
  } catch {
    /* An invalid or cross-World readback cannot authorize navigation. */
  }
  return {
    ok: false,
    kind: "transport",
    reason: "The World did not confirm the exact room and session.",
  };
}

/**
 * RM9: open a ROOM with its first session — one atomic room.opened +
 * session.opened batch through the open-room door, confirmed by the World's
 * read of that room and session. A workroom is 0..1 per (World,
 * workroomKey); the door answers the standing one on a repeat.
 */
export function openWorldRoomEntry(
  input: {
    readonly worldKey: string;
    readonly roomId: string;
    readonly label: string;
    readonly kind: "general" | "workroom";
    readonly workroomKey?: string;
    readonly sessionId: string;
    readonly goalId?: string;
    readonly goalIds?: ReadonlyArray<string>;
  },
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldRoomEntryOutcome> {
  return openEntry(
    "/api/neucleos/world/rooms/open",
    input,
    (entry) =>
      entry.room.roomId === input.roomId &&
      entry.room.kind === input.kind &&
      canonicalWorkroomLabel(entry.room.workroomKey, entry.room.label) ===
        canonicalWorkroomLabel(entry.room.workroomKey, input.label) &&
      (entry.room.workroomKey ?? undefined) === input.workroomKey &&
      (input.goalIds === undefined ||
        JSON.stringify(entry.room.goalIds) === JSON.stringify(input.goalIds)) &&
      entry.session.parentId === input.roomId,
    fetchImpl,
  );
}

/** Compact opening is for a root session in a known room; forks retain their full path. */
export function openWorldSessionEntry(
  input: {
    readonly worldKey: string;
    readonly parentId: string;
    readonly sessionId: string;
    readonly label: string;
  },
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldRoomEntryOutcome> {
  return openEntry(
    "/api/neucleos/world/sessions/open",
    input,
    (entry) =>
      entry.room.roomId === input.parentId &&
      entry.session.parentId === input.parentId &&
      entry.session.label === input.label,
    fetchImpl,
  );
}

/**
 * The closed refusal vocabulary birth consumers may lawfully switch on
 * (JB2/JB6, audit correction 2). Mirrors the Desk door's
 * CanvasReadRefusalKind plus the client's own transport mint; anything else
 * normalizes to "unrecognized" — a typed absence, never an invented member,
 * and never a substring match over the human reason.
 */
export const WORLD_SLICE_DOOR_REFUSAL_KINDS = [
  "not_signed_in",
  "world_unresolved",
  "grant_stale",
  "tool_not_on_grant",
  "grant_rejected",
  "upstream_refusal",
  "context_changed",
  "transport",
  "module_integrity",
  "bad_request",
] as const;

export type WorldSessionDoorRefusalKind =
  | (typeof WORLD_SLICE_DOOR_REFUSAL_KINDS)[number]
  | "unrecognized";

export function normalizeWorldSessionDoorRefusalKind(raw: string): WorldSessionDoorRefusalKind {
  return (WORLD_SLICE_DOOR_REFUSAL_KINDS as readonly string[]).includes(raw)
    ? (raw as WorldSessionDoorRefusalKind)
    : "unrecognized";
}

/**
 * The compact parent-selection claim sent through the loopback HTTP door.
 * The World verifies a stored receipt inside the birth transaction. Legacy
 * clients may still carry a catalog claim until their selection is refreshed.
 */
export type WorldContextBirthClaim =
  | WorldSessionSelectionBirth
  | (Pick<
      WorldContextSelection,
      | "roomId"
      | "sessionId"
      | "agentEnvironmentId"
      | "agentThreadId"
      | "selectionCut"
      | "selectionDigest"
    > & {
      readonly schemaVersion: "world-context-selection-v1";
      readonly catalogDigest: string;
      readonly catalogCustodyDigest: string;
    });

export function contextBirthClaimOf(
  entry: Extract<RoomUniverseEntry, { state: "complete" | "paged" | "selection" }>,
): WorldContextBirthClaim {
  const coordinate = {
    roomId: entry.coordinate.roomId,
    sessionId: entry.coordinate.sessionId,
    agentEnvironmentId: entry.coordinate.agentEnvironmentId,
    agentThreadId: entry.coordinate.agentThreadId,
    selectionCut: entry.selection.selectionCut,
    selectionDigest: entry.selection.selectionDigest,
  };
  if (
    "schemaVersion" in entry.selection &&
    entry.selection.schemaVersion === "world-session-selection-v1"
  )
    return { schemaVersion: "world-session-selection-v1", ...coordinate };
  if (!("catalogDigest" in entry)) throw new Error("The parent selection receipt is not readable.");
  return {
    schemaVersion: "world-context-selection-v1",
    ...coordinate,
    catalogDigest: entry.catalogDigest,
    catalogCustodyDigest: entry.catalogCustodyDigest,
  };
}

/** Birth reserves the same gesture queue as Send. Only a preceding verified
 * gesture may advance the clicked receipt; browsing and remote edits cannot. */
export function orderContextBirthSelection<T>(
  entry: Extract<RoomUniverseEntry, { state: "complete" | "paged" | "selection" }>,
  consume: (claim: WorldContextBirthClaim) => Promise<T>,
  refuse: (reason: string) => T,
): Promise<T> {
  const captured = contextBirthClaimOf(entry);
  return orderSessionSelection(
    entry.coordinate,
    { selectionCut: captured.selectionCut, selectionDigest: captured.selectionDigest },
    async (order) => {
      if (order.failed || order.expected === undefined)
        return refuse("A preceding selection gesture failed. Review the selection and retry.");
      if (
        captured.schemaVersion !== "world-session-selection-v1" &&
        (captured.selectionCut !== order.expected.selectionCut ||
          captured.selectionDigest !== order.expected.selectionDigest)
      )
        return refuse("The parent selection changed. Review it before creating a session.");
      return consume({
        ...captured,
        selectionCut: order.expected.selectionCut,
        selectionDigest: order.expected.selectionDigest,
      });
    },
  );
}

export interface WorldSessionForkedFromInput {
  readonly sessionId: string;
  readonly threadId: string;
  readonly snapshotRef: string;
}

function refusal(
  response: Response,
  body: Record<string, unknown> | null,
): Extract<WorldSessionClientOutcome, { ok: false }> {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string"
        ? body["reason"]
        : "The authoritative World Session projection is unavailable.",
  };
}

function parsedIndex(value: unknown): WorldRoomIndexValue | null {
  try {
    return decodeWorldRoomIndex(value);
  } catch {
    return null;
  }
}

/** What the pages read so far hold. Never a complete directory: parent
 * links, Harmony and uniqueness across the World are validated only once the
 * last page is in. */
export interface WorldRoomPagesProgress {
  readonly worldKey: string;
  readonly head: number;
  readonly cut: number;
  readonly rootRoomId: WorldRoomIndexValue["rootRoomId"];
  readonly rooms: WorldRoomIndexValue["rooms"];
  readonly sessions: WorldRoomIndexValue["sessions"];
}

/** One caller of a room index read: what it watches, and whether it still wants the answer. */
interface RoomIndexReader {
  readonly isCurrent?: () => boolean;
  readonly signal?: AbortSignal;
  readonly onPage?: (progress: WorldRoomPagesProgress) => void;
  /** Its own onPage threw: that caller's read fails with it, the others go on. */
  failed?: { readonly cause: unknown };
}

const readerCurrent = (reader: RoomIndexReader) =>
  reader.failed === undefined && reader.signal?.aborted !== true && reader.isCurrent?.() !== false;

const roomReadChanged = (): WorldSessionClientOutcome => ({
  ok: false,
  kind: "context_changed",
  reason: "The World or signed-in access changed during the room read.",
});

/**
 * One page chain in flight for a World (W-35fbf22c). Callers that ask for the
 * same World under the same signed-in access before its first request is sent
 * share it instead of paging the whole directory again. The World fixes a
 * chain's cut when it handles that request, so a caller arriving after it was
 * sent may know of a later change: it starts its own chain. A chain stops,
 * and aborts its request, once none of its callers still wants the answer.
 */
interface RoomIndexFlight {
  readonly readers: Set<RoomIndexReader>;
  /** Its first request has gone to the World: nobody joins any more. */
  sent: boolean;
  outcome: Promise<WorldSessionClientOutcome>;
}

/**
 * The last complete index read for a World, with the exact watermark its
 * pages were pinned to: the World, the Actor and grant that read it, the
 * projection generation, the ledger head and cut, and the Goal catalog
 * revision. Kept only in memory, for the most recent HELD_WORLDS Worlds of the
 * signed-in account, and never shown on its own: a later read takes the
 * World's fresh first page and answers with this index only when that page
 * carries the same watermark and the same records; anything else reads the
 * whole directory again.
 *
 * Goals are read live into room rows, and a Goal insert appends no move, so
 * head and cut alone never vouch for a row. An index is held only when every
 * one of its pages carried the same goalCatalogRevision; a World that does
 * not send it is read in full every time. Sign-out, an account switch and a
 * World leaving the account drop what was held.
 */
interface HeldRoomIndex {
  readonly transport: typeof fetch;
  readonly pin: string;
  readonly firstPage: string;
  readonly index: WorldRoomIndexValue;
}

const HELD_WORLDS = 16;
const roomIndexFlights = new WeakMap<typeof fetch, Map<string, RoomIndexFlight>>();
const heldRoomIndexes = new Map<string, HeldRoomIndex>();
let heldAccount: string | undefined;
let heldWatch: (() => void) | undefined;
/**
 * Advances whenever signed-in access is invalidated: sign-out, an account
 * switch, a World leaving the account, a grant change. A read started under an
 * earlier epoch keeps nothing and answers nothing but "access changed".
 */
let accessEpoch = 0;

/** Forget every held room index (sign-out, account switch). */
export function forgetHeldRoomIndexes(): void {
  accessEpoch++;
  heldRoomIndexes.clear();
}

const roomReadAccessChanged = (): WorldSessionClientOutcome => ({
  ok: false,
  kind: "context_changed",
  reason: "The signed-in access changed during the room read. Read it again.",
});

/** The access key of `worldKey` as the store holds it now; null when unreadable. */
function accessKeyOf(worldKey: string): string | null {
  try {
    const state = useNeucleosWorldStore.getState();
    const world = state.worlds.find((row) => row.worldKey === worldKey);
    return JSON.stringify([
      JSON.stringify([state.signedIn, state.identity]),
      world?.actorId ?? null,
      world?.grantKey ?? world?.expiresAt ?? null,
      state.grantRevision,
    ]);
  } catch {
    return null;
  }
}

/** Access a read was started under is still the access now. */
const accessStill = (epoch: number, worldKey: string, access: string | null) =>
  accessEpoch === epoch && accessKeyOf(worldKey) === access;

function holdRoomIndex(worldKey: string, held: HeldRoomIndex) {
  heldRoomIndexes.delete(worldKey);
  heldRoomIndexes.set(worldKey, held);
  for (const oldest of heldRoomIndexes.keys()) {
    if (heldRoomIndexes.size <= HELD_WORLDS) break;
    heldRoomIndexes.delete(oldest);
  }
}

/**
 * The signed-in access a read runs under, from the Desk's session store: who
 * is signed in, and the Actor and grant this World is read with. Reads share a
 * chain only under the same access. Null when the store cannot be read, and
 * then nothing is shared.
 */
function roomReadAccess(worldKey: string): string | null {
  try {
    const state = useNeucleosWorldStore.getState();
    const account = JSON.stringify([state.signedIn, state.identity]);
    if (heldAccount !== undefined && heldAccount !== account) forgetHeldRoomIndexes();
    heldAccount = account;
    if (heldWatch === undefined) {
      heldWatch = useNeucleosWorldStore.subscribe((next, previous) => {
        const nextAccount = JSON.stringify([next.signedIn, next.identity]);
        if (nextAccount !== heldAccount) {
          heldAccount = nextAccount;
          forgetHeldRoomIndexes();
          return;
        }
        const worlds = new Set(next.worlds.map((row) => row.worldKey));
        if (
          next.grantRevision !== previous.grantRevision ||
          previous.worlds.some((row) => !worlds.has(row.worldKey))
        )
          accessEpoch++;
        for (const key of [...heldRoomIndexes.keys()])
          if (!worlds.has(key)) heldRoomIndexes.delete(key);
      });
    }
  } catch {
    return null;
  }
  return accessKeyOf(worldKey);
}

/**
 * Read a World's complete room index, page by page (the World answers at most
 * 64 records a page, its largest). Callers for the same World and signed-in
 * access that ask before the read's first request is sent share one page
 * chain. A World whose ledger and Goal catalog have not moved since its last
 * complete read here costs one page: the first page confirms the watermark and
 * the held index is the answer.
 */
export async function readWorldRoomIndex(
  worldKey: string,
  fetchImpl: typeof fetch = fetchPrimary,
  options: {
    readonly isCurrent?: () => boolean;
    readonly signal?: AbortSignal;
    /** Called after each validated page with everything read so far, in birth order. */
    readonly onPage?: (progress: WorldRoomPagesProgress) => void;
  } = {},
): Promise<WorldSessionClientOutcome> {
  const reader: RoomIndexReader = {
    ...(options.isCurrent === undefined ? {} : { isCurrent: options.isCurrent }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    ...(options.onPage === undefined ? {} : { onPage: options.onPage }),
  };
  if (!readerCurrent(reader)) return roomReadChanged();
  const access = roomReadAccess(worldKey);
  const epoch = accessEpoch;
  const flights = roomIndexFlights.get(fetchImpl) ?? new Map<string, RoomIndexFlight>();
  roomIndexFlights.set(fetchImpl, flights);
  const key = access === null ? null : JSON.stringify([worldKey, access]);
  let flight = key === null ? undefined : flights.get(key);
  if (flight !== undefined && !flight.sent) flight.readers.add(reader);
  else {
    const started: RoomIndexFlight = {
      readers: new Set([reader]),
      sent: false,
      outcome: Promise.resolve(roomReadChanged()),
    };
    if (key !== null) flights.set(key, started);
    const settle = () => {
      if (key !== null && flights.get(key) === started) flights.delete(key);
    };
    started.outcome = readRoomIndexPages(worldKey, fetchImpl, started, epoch, access).finally(
      settle,
    );
    flight = started;
  }
  const outcome = await flight.outcome;
  if (reader.failed !== undefined) throw reader.failed.cause;
  if (!readerCurrent(reader)) return roomReadChanged();
  // Never hand a caller rooms read under access that has since changed.
  if (outcome.ok && !accessStill(epoch, worldKey, access)) return roomReadAccessChanged();
  return outcome;
}

async function readRoomIndexPages(
  worldKey: string,
  fetchImpl: typeof fetch,
  flight: RoomIndexFlight,
  epoch: number,
  access: string | null,
): Promise<WorldSessionClientOutcome> {
  // Callers asking in the same turn (effects of one commit, StrictMode's
  // second mount) join before the first request is sent.
  await Promise.resolve();
  const abort = new AbortController();
  let accessLost = false;
  const current = () => {
    if (!accessStill(epoch, worldKey, access)) accessLost = true;
    else if ([...flight.readers].some(readerCurrent)) return true;
    abort.abort();
    return false;
  };
  const onAbort = () => void current();
  const watched = new Set<AbortSignal>();
  const changed = () => (accessLost ? roomReadAccessChanged() : roomReadChanged());
  const drop = () => {
    if (heldRoomIndexes.get(worldKey)?.transport === fetchImpl) heldRoomIndexes.delete(worldKey);
  };
  const invalid = (): WorldSessionClientOutcome => {
    drop();
    return {
      ok: false,
      kind: "transport",
      reason: "The local Cleo server returned an invalid Session index page.",
    };
  };
  const publish = (progress: WorldRoomPagesProgress) => {
    for (const reader of flight.readers) {
      if (!readerCurrent(reader) || reader.onPage === undefined) continue;
      try {
        reader.onPage(progress);
      } catch (cause) {
        reader.failed = { cause };
      }
    }
  };
  const rooms: WorldRoomIndexValue["rooms"][number][] = [];
  const sessions: WorldRoomIndexValue["sessions"][number][] = [];
  const roomIds = new Set<string>(),
    sessionIds = new Set<string>(),
    cursors = new Set<string>();
  let cursor: string | undefined, pin: string | undefined, firstPage: string | undefined;
  // The Goal catalog revision every page so far carried; null once a page
  // carried none or a different one, and the index is then not held.
  let goalRevision: number | null | undefined;
  try {
    while (current()) {
      for (const reader of flight.readers)
        if (reader.signal !== undefined && !watched.has(reader.signal)) {
          watched.add(reader.signal);
          reader.signal.addEventListener("abort", onAbort, { once: true });
        }
      flight.sent = true;
      let response: Response;
      try {
        response = await postLocalJson(
          "/api/neucleos/world/rooms",
          {
            worldKey,
            page: cursor === undefined ? {} : { cursor },
          },
          fetchImpl,
          abort.signal,
        );
      } catch {
        return current()
          ? { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." }
          : changed();
      }
      const body = await decodeJsonObject(response);
      if (!current()) return changed();
      if (!response.ok || body?.["ok"] !== true) {
        // A refusal (revoked access, sign-out, another World) drops what was held.
        drop();
        return refusal(response, body);
      }
      let page: WorldRoomIndexPage;
      try {
        page = decodeWorldRoomIndexPage(body["page"]);
      } catch {
        return invalid();
      }
      const nextPin = JSON.stringify([
        page.worldKey,
        page.actorId,
        page.grantId,
        page.generation,
        page.head,
        page.cut,
        page.rootRoomId,
      ]);
      if (page.worldKey !== worldKey || (pin !== undefined && pin !== nextPin)) return invalid();
      const pageGoals = page.goalCatalogRevision ?? null;
      goalRevision = goalRevision === undefined || goalRevision === pageGoals ? pageGoals : null;
      if (pin === undefined) {
        firstPage = JSON.stringify([page.rooms, page.sessions, page.continueCursor]);
        const last = heldRoomIndexes.get(worldKey);
        // The World's own fresh first page at the same watermark and Goal
        // catalog revision: nothing changed since the held index was read, so
        // none of it is read again.
        if (
          last !== undefined &&
          last.transport === fetchImpl &&
          pageGoals !== null &&
          last.pin === JSON.stringify([nextPin, pageGoals]) &&
          last.firstPage === firstPage
        ) {
          if (!current()) return changed();
          holdRoomIndex(worldKey, last);
          return { ok: true, index: last.index };
        }
      }
      pin = nextPin;
      for (const room of page.rooms) {
        if (roomIds.has(room.roomId)) return invalid();
        roomIds.add(room.roomId);
        rooms.push(room);
      }
      for (const session of page.sessions) {
        if (sessionIds.has(session.sessionId)) return invalid();
        sessionIds.add(session.sessionId);
        sessions.push(session);
      }
      if ([...flight.readers].some((reader) => reader.onPage !== undefined)) {
        publish({
          worldKey: page.worldKey,
          head: page.head,
          cut: page.cut,
          rootRoomId: page.rootRoomId,
          rooms: [...rooms].sort(
            (a, b) => a.bornSeq - b.bornSeq || a.roomId.localeCompare(b.roomId),
          ),
          sessions: [...sessions].sort(
            (a, b) => a.bornSeq - b.bornSeq || a.sessionId.localeCompare(b.sessionId),
          ),
        });
        if (!current()) return changed();
      }
      if (page.continueCursor === null) {
        const index = parsedIndex({
          ...page,
          schemaVersion: "world-room-index-v1",
          rooms: rooms.sort((a, b) => a.bornSeq - b.bornSeq || a.roomId.localeCompare(b.roomId)),
          sessions: sessions.sort(
            (a, b) => a.bornSeq - b.bornSeq || a.sessionId.localeCompare(b.sessionId),
          ),
        });
        if (index === null) return invalid();
        // Keep and answer only under the access the read started with.
        if (!current()) return changed();
        if (goalRevision === null || goalRevision === undefined) drop();
        else
          holdRoomIndex(worldKey, {
            transport: fetchImpl,
            pin: JSON.stringify([nextPin, goalRevision]),
            firstPage: firstPage!,
            index,
          });
        return { ok: true, index };
      }
      if (cursors.has(page.continueCursor)) return invalid();
      cursors.add(page.continueCursor);
      cursor = page.continueCursor;
    }
    return changed();
  } finally {
    flight.sent = true;
    for (const signal of watched) signal.removeEventListener("abort", onAbort);
  }
}

export async function openWorldSession(
  input: {
    readonly worldKey: string;
    readonly sessionId: string;
    readonly label: string;
    /** The room (R_) or session (S_) the new session hangs under — required by the door. */
    readonly parentId: string;
    readonly goalId?: string;
    readonly goalIds?: ReadonlyArray<string>;
    readonly assignment?: string;
    readonly contextBirth?: WorldContextBirthClaim;
    readonly forkedFrom?: WorldSessionForkedFromInput;
    /**
     * SB3: accepted for the birth flow's carriage; deliberately NOT sent to
     * the World door — the board reference's durable home is the local
     * thread projection plus the JB1 attempt record, so the door payload
     * stays byte-identical to pre-SB3. A World-Session-row board identity is
     * a named absence for a later leaf.
     */
    readonly workroomKey?: string | null;
  },
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldRoomEntryOutcome> {
  // The door confirms the birth with this one entry (the session and its
  // room), never the World directory: the whole directory, read right after
  // the write, outlived the door's time limit on a World of 752 sessions.
  const { workroomKey: _localOnly, ...wire } = input;
  return openEntry(
    "/api/neucleos/world/sessions/open",
    wire,
    (entry) => {
      const exact = entry.session;
      return (
        (!input.parentId.startsWith("R_") || entry.room.roomId === input.parentId) &&
        exact.label === input.label &&
        exact.parentId === input.parentId &&
        (input.goalId === undefined || exact.goalId === input.goalId) &&
        (input.forkedFrom === undefined ||
          (exact.forkedFrom?.sessionId === input.forkedFrom.sessionId &&
            exact.forkedFrom?.threadId === input.forkedFrom.threadId &&
            exact.forkedFrom?.snapshotRef === input.forkedFrom.snapshotRef))
      );
    },
    fetchImpl,
  );
}

/**
 * The engagement flip (NV2): close sends a Session headless, re-engage brings
 * it back. Success is claimed only when the World's read of that session
 * carries the flipped engagement. Close is never stop.
 */
export function setWorldSessionEngagement(
  input: {
    readonly worldKey: string;
    readonly sessionId: string;
    readonly engagement: "headless" | "engaged";
  },
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldEntryChangeOutcome> {
  return confirmChangedEntry(
    "/api/neucleos/world/sessions/engagement",
    input,
    input.sessionId,
    (entry) => entry.session.engagement === input.engagement,
    fetchImpl,
  );
}

// GP2 — the pictured goal process (the principal's sixth drawing): the goal
// tree read and the one-sub-goal mint, through the same loopback relay as
// every World Session call. The bearer never reaches the browser.

export interface WorldGoalRow extends Partial<GoalRelationships> {
  readonly goalId: string;
  readonly parent: string | null;
  readonly title: string;
  readonly statement: string;
  readonly state: "open" | "closed";
  /**
   * BOARD1 (register §38, D-BOARD1): the Goal's own workflow step, exactly as
   * the World door projects it — the World Board folds its column from this
   * at read and stores nothing.
   */
  readonly step: WorldGoalStep;
  readonly cleoCan: string | null;
}

export type WorldGoalStep = WorldRoomGoalProjection["storedStep"];
const WORLD_GOAL_STEP_SET: ReadonlySet<string> = new Set<WorldGoalStep>([
  "comprehend",
  "conceive",
  "decide",
  "design",
  "build",
  "run",
]);
export const isWorldGoalStep = (value: unknown): value is WorldGoalStep =>
  typeof value === "string" && WORLD_GOAL_STEP_SET.has(value);

export type WorldGoalsOutcome =
  | { readonly ok: true; readonly goals: ReadonlyArray<WorldGoalRow> }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

function goalRefusal(
  response: Response,
  body: Record<string, unknown> | null,
): { readonly ok: false; readonly kind: string; readonly reason: string } {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string"
        ? body["reason"]
        : "The World's goal tree is unavailable.",
  };
}

function parsedGoalRows(value: unknown): ReadonlyArray<WorldGoalRow> | null {
  if (!Array.isArray(value)) return null;
  const rows: Array<WorldGoalRow> = [];
  for (const item of value) {
    if (item === null || typeof item !== "object" || Array.isArray(item)) return null;
    const row = item as Record<string, unknown>;
    const goalId = row["goalId"];
    const parent = row["parent"];
    const title = row["title"];
    const statement = row["statement"];
    const state = row["state"];
    const step = row["step"];
    const cleoCan = row["cleoCan"];
    if (typeof goalId !== "string" || goalId.length === 0) return null;
    if (parent !== null && typeof parent !== "string") return null;
    if (typeof title !== "string" || typeof statement !== "string") return null;
    if (state !== "open" && state !== "closed") return null;
    if (!isWorldGoalStep(step)) return null;
    if (cleoCan !== null && typeof cleoCan !== "string") return null;
    const relationships = parseGoalRelationships(row);
    if (!relationships) return null;
    rows.push({ goalId, parent, title, statement, state, step, cleoCan, ...relationships });
  }
  return rows;
}

export async function readWorldGoals(
  worldKey: string,
  fetchImpl: typeof fetch = fetchPrimary,
  signal?: AbortSignal,
): Promise<WorldGoalsOutcome> {
  let response: Response;
  try {
    response = await postLocalJson("/api/neucleos/world/goals", { worldKey }, fetchImpl, signal);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return goalRefusal(response, body);
  const goals = parsedGoalRows(body["goals"]);
  return goals === null
    ? {
        ok: false,
        kind: "transport",
        reason: "The local Cleo server returned an invalid goal tree.",
      }
    : { ok: true, goals };
}

export interface MintWorldSubGoalInput {
  readonly worldKey: string;
  readonly parentGoalId: string;
  readonly goalId: string;
  readonly title: string;
  readonly statement: string;
  /** One Goal, one Cleo Can — the mint carries the observable acceptance predicate. */
  readonly cleoCan: string;
}

export type MintWorldSubGoalOutcome =
  | { readonly ok: true; readonly created: boolean }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

export async function mintWorldSubGoal(
  input: MintWorldSubGoalInput,
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<MintWorldSubGoalOutcome> {
  let response: Response;
  try {
    response = await postLocalJson(
      "/api/neucleos/world/goals/mint",
      {
        worldKey: input.worldKey,
        parentGoalId: input.parentGoalId,
        goalId: input.goalId,
        title: input.title,
        statement: input.statement,
        cleoCan: input.cleoCan,
      },
      fetchImpl,
    );
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return goalRefusal(response, body);
  const minted = body["minted"];
  const created =
    minted !== null && typeof minted === "object" && !Array.isArray(minted)
      ? (minted as Record<string, unknown>)["created"] === true
      : false;
  return { ok: true, created };
}

export interface GoalRelationshipInput {
  worldKey: string;
  goalId: string;
  relation: "parent" | "depends_on" | "context";
  targetId: string;
  linked: boolean;
  reason: string;
  source: "human" | "cleo";
  expectedRelationSeq: number;
}
export async function setGoalRelationship(
  input: GoalRelationshipInput,
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldGoalsOutcome> {
  try {
    const response = await fetchImpl("/api/neucleos/world/goals/relationship", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    const body = (await response.json()) as Record<string, unknown>;
    if (!response.ok || body["ok"] !== true) return goalRefusal(response, body);
    const goals = parsedGoalRows(body["goals"]);
    return goals
      ? { ok: true, goals }
      : { ok: false, kind: "transport", reason: "Invalid goal readback." };
  } catch {
    return {
      ok: false,
      kind: "transport",
      reason: "The relationship could not be confirmed. Refresh before retrying.",
    };
  }
}
/** A room's focus, confirmed by the World's read of the room with the root session named. */
export async function setRoomFocus(
  input: {
    worldKey: string;
    roomId: string;
    goalIds: ReadonlyArray<string>;
    contribution: string;
    expectedFocusSeq: number;
    /** One of the room's root sessions (rootSessionIdOf), read back with the room. */
    sessionId: string;
  },
  fetchImpl: typeof fetch = fetchPrimary,
): Promise<WorldEntryChangeOutcome> {
  try {
    const response = await fetchImpl("/api/neucleos/world/rooms/focus", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    const body = (await response.json()) as Record<string, unknown>;
    if (!response.ok || body["ok"] !== true) return goalRefusal(response, body);
    const entry = Schema.decodeUnknownSync(WorldRoomEntry)(body["entry"]);
    return entry.worldKey === input.worldKey &&
      entry.room.roomId === input.roomId &&
      entry.session.sessionId === input.sessionId &&
      JSON.stringify(entry.room.goalIds) === JSON.stringify(input.goalIds) &&
      entry.room.contribution === input.contribution
      ? { ok: true, entry }
      : { ok: false, kind: "transport", reason: "Invalid room focus readback." };
  } catch {
    return {
      ok: false,
      kind: "transport",
      reason: "The room focus could not be confirmed. Refresh before retrying.",
    };
  }
}
